import React from 'react';
import { Wallet, TrendingUp, TicketPercent, HelpCircle } from 'lucide-react';
import type { ProductFormData } from '@/modelos/productos/Producto';
import type { FormError } from '@/hooks/productos/useProductoFormulario';
import Tooltip from '@/contasis/components/Tooltip/Tooltip';
import { Input } from '@/contasis';

interface ProductFinancialSectionProps {
  formData: ProductFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProductFormData>>;
  errors: FormError;
  isFieldVisible: (fieldId: string) => boolean;
  isFieldRequired: (fieldId: string) => boolean;
  onBlur?: () => void;
  showCheck?: boolean;
  renderCheck?: (className?: string) => React.ReactNode;
}

export const ProductPurchasePriceField: React.FC<ProductFinancialSectionProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  onBlur,
  showCheck,
  renderCheck
}) => {
  if (!isFieldVisible('precioCompra')) return null;

  return (
    <Input
      id="precioCompra"
      label="Precio inicial de compra"
      required={isFieldRequired('precioCompra')}
      labelAction={
        <Tooltip text="Costo referencial para márgenes. No afecta stock por sí solo." position="right">
          <button
            type="button"
            aria-label="Ayuda: Precio inicial de compra"
            className="inline-flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </Tooltip>
      }
      type="number"
      step={0.01}
      min={0}
      value={formData.precioCompra}
      onChange={(e) => setFormData(prev => ({ ...prev, precioCompra: parseFloat(e.target.value) || 0 }))}
      onBlur={onBlur}
      leftIcon={<span className="flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /><span className="text-xs font-medium">S/</span></span>}
      rightIcon={showCheck ? renderCheck?.() : undefined}
      error={errors.precioCompra}
      size="small"
      placeholder="0.00"
    />
  );
};

export const ProductProfitPercentField: React.FC<ProductFinancialSectionProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  onBlur,
  showCheck,
  renderCheck
}) => {
  if (!isFieldVisible('porcentajeGanancia')) return null;

  return (
    <Input
      id="porcentajeGanancia"
      label="Porcentaje de ganancia"
      required={isFieldRequired('porcentajeGanancia')}
      labelAction={
        <Tooltip text="Se usa para sugerir precio de venta desde el costo." position="right">
          <button
            type="button"
            aria-label="Ayuda: Porcentaje de ganancia"
            className="inline-flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </Tooltip>
      }
      type="number"
      step={0.01}
      min={0}
      max={100}
      value={formData.porcentajeGanancia}
      onChange={(e) =>
        setFormData(prev => ({ ...prev, porcentajeGanancia: parseFloat(e.target.value) || 0 }))
      }
      onBlur={onBlur}
      leftIcon={<TrendingUp className="w-3.5 h-3.5" />}
      rightIcon={showCheck ? renderCheck?.() : <span className="text-xs font-medium">%</span>}
      error={errors.porcentajeGanancia}
      size="small"
      placeholder="0.00"
    />
  );
};

export const ProductDiscountField: React.FC<ProductFinancialSectionProps> = ({
  formData,
  setFormData,
  errors,
  isFieldVisible,
  isFieldRequired,
  onBlur,
  showCheck,
  renderCheck
}) => {
  if (!isFieldVisible('descuentoProducto')) return null;

  return (
    <Input
      id="descuentoProducto"
      label="Descuento del producto"
      required={isFieldRequired('descuentoProducto')}
      labelAction={
        <Tooltip text="Descuento sugerido por defecto (puede cambiarse en la venta)." position="right">
          <button
            type="button"
            aria-label="Ayuda: Descuento del producto"
            className="inline-flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </Tooltip>
      }
      type="number"
      step={0.01}
      min={0}
      max={100}
      value={formData.descuentoProducto}
      onChange={(e) =>
        setFormData(prev => ({ ...prev, descuentoProducto: parseFloat(e.target.value) || 0 }))
      }
      onBlur={onBlur}
      leftIcon={<TicketPercent className="w-3.5 h-3.5" />}
      rightIcon={showCheck ? renderCheck?.() : <span className="text-xs font-medium">%</span>}
      error={errors.descuentoProducto}
      size="small"
      placeholder="0.00"
    />
  );
};


